// ACL constants inlined (originally from @servicenow/sdk-core/runtime/app)
const AclAttributes = {
    user_is_authenticated: '239bdf0c47d23110ba46ff58936d4330',
    has_admin_role: '8637f144b732111074f706dd1e11a981',
}

const AclOperations = {
    execute: 'execute',
} as const

// Table name constants
export const TABLE_NAMES = {
    SN_NOWASSIST_SKILL_CONFIG: 'sn_nowassist_skill_config',
    SN_NOWASSIST_SKILL_CONFIG_STATUS: 'sn_nowassist_skill_config_status',
    SN_NOWASSIST_SKILL_CONFIG_VAR_SET: 'sn_nowassist_skill_config_var_set',
    SN_NOWASSIST_SKILL_CONFIG_APPLICABILITY: 'sn_nowassist_skill_config_applicability',
    SN_NOWASSIST_SKILL_CONFIG_UI: 'sn_ns_skill_config_ui',
    SYS_GENERATIVE_AI_PROMPT_CONFIG: 'sys_generative_ai_prompt_config',
    SYS_GENERATIVE_AI_CONFIG: 'sys_generative_ai_config',
    SYS_GENERATIVE_AI_VALIDATOR: 'sys_generative_ai_validator',
    SYS_GENERATIVE_AI_REQUEST_VALIDATOR: 'sys_generative_ai_request_validator',
    SYS_GENERATIVE_AI_RESPONSE_VALIDATOR: 'sys_generative_ai_response_validator',
    SYS_GEN_AI_FEATURE_MAPPING: 'sys_gen_ai_feature_mapping',
    SYS_GEN_AI_STRATEGY_MAPPING: 'sys_gen_ai_strategy_mapping',
    SYS_GEN_AI_SKILL: 'sys_gen_ai_skill',
    SYS_GEN_AI_SKILL_APPLICABILITY: 'sys_gen_ai_skill_applicability',
    SYS_GEN_AI_SKILL_CONFIG: 'sys_gen_ai_skill_config',
    SYS_ONE_EXTEND_CAPABILITY: 'sys_one_extend_capability',
    SYS_ONE_EXTEND_CAPABILITY_DEFINITION: 'sys_one_extend_capability_definition',
    SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE: 'sys_one_extend_definition_attribute',
    SYS_ONE_EXTEND_DEFINITION_CONFIG: 'sys_one_extend_definition_config',
    SYS_VARIABLE_VALUE: 'sys_variable_value',
    // Tool-related tables
    SYS_ONE_EXTEND_RESOURCE_MAPPING: 'sys_one_extend_resource_mapping',
    SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING: 'sys_one_extend_resource_attribute_mapping',
    SYS_ONE_EXTEND_RESOURCE_EDGE: 'sys_one_extend_resource_edge',
} as const

/**
 * "Others" skill family sys_id - the default when no skillFamily is specified
 */
export const OTHERS_SKILL_FAMILY_SYS_ID = 'a5efaf1c93764210aa5730f16489186d'

/**
 * Default field values for all NowAssist tables.
 * Organized by table name for direct lookup in getField() helper function.
 * Each table's defaults are applied automatically when fields are not explicitly provided.
 *
 * IMPORTANT: These defaults are ALWAYS applied on every deploy.
 * For fields that should only be set on initial creation (and preserved on re-deploy),
 * use TABLE_CREATE_ONLY_DEFAULTS instead.
 */
export const TABLE_DEFAULTS: Record<string, Record<string, unknown>> = {
    [TABLE_NAMES.SN_NOWASSIST_SKILL_CONFIG]: {
        state: '0',
        skill_family: OTHERS_SKILL_FAMILY_SYS_ID,
    },
    [TABLE_NAMES.SN_NOWASSIST_SKILL_CONFIG_STATUS]: {},
    [TABLE_NAMES.SYS_ONE_EXTEND_CAPABILITY]: {
        active: true,
        type: 'skill',
    },
    [TABLE_NAMES.SYS_ONE_EXTEND_CAPABILITY_DEFINITION]: {
        truncate_strategy: '2b99e1137fb20210f465c3b76d86650d',
    },
    [TABLE_NAMES.SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE]: {
        data_type: 'string',
        type: 'input',
        active: true,
    },
    [TABLE_NAMES.SYS_GENERATIVE_AI_CONFIG]: {
        max_tokens: '500',
        min_word_count: '-1',
        state: 'draft',
        active: false,
        version: 1,
        temperature: 0.2,
    },
    [TABLE_NAMES.SYS_GENERATIVE_AI_PROMPT_CONFIG]: {
        is_default: false,
    },
    [TABLE_NAMES.SYS_GEN_AI_FEATURE_MAPPING]: {
        active: true,
    },
    [TABLE_NAMES.SYS_GEN_AI_STRATEGY_MAPPING]: {
        active: true,
    },
    [TABLE_NAMES.SYS_GENERATIVE_AI_VALIDATOR]: {},
    [TABLE_NAMES.SYS_GENERATIVE_AI_REQUEST_VALIDATOR]: {},
    [TABLE_NAMES.SYS_GENERATIVE_AI_RESPONSE_VALIDATOR]: {},
    [TABLE_NAMES.SYS_ONE_EXTEND_DEFINITION_CONFIG]: {
        active: true,
    },
    [TABLE_NAMES.SN_NOWASSIST_SKILL_CONFIG_VAR_SET]: {
        hide_step: false,
    },
    [TABLE_NAMES.SYS_VARIABLE_VALUE]: {
        document: 'sn_nowassist_skill_config_var_set',
    },
    [TABLE_NAMES.SYS_GEN_AI_SKILL_APPLICABILITY]: {
        condition_mode: 'simple',
    },
    // Tool-related table defaults
    [TABLE_NAMES.SYS_ONE_EXTEND_RESOURCE_MAPPING]: {
        active: true,
    },
    [TABLE_NAMES.SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING]: {
        attribute_value_type: 'template',
    },
    [TABLE_NAMES.SYS_ONE_EXTEND_RESOURCE_EDGE]: {
        name: 'edge',
        active: true,
        order: '0',
    },
} as const

/**
 * Default role applied in simple mode when the author provides no roles.
 * Advanced mode does not apply this default — the condition script alone governs access.
 */
export const SYS_GEN_AI_SKILL_APPLICABILITY_CREATE_ONLY_DEFAULTS = {
    roles: 'now_assist_panel_user',
} as const

export const SYS_ONE_EXTEND_CAPABILITY_DEFINITION_API_TYPES = {
    SYS_SCRIPT_INCLUDE: 'sys_script_include',
    SYS_HUB_FLOW: 'sys_hub_flow',
    SYS_HUB_ACTION_TYPE_DEFINITION: 'sys_hub_action_type_definition',
}

/**
 * Script tool API ID for detection
 * Used to distinguish Script tool from FlowAction (both use SYS_HUB_ACTION_TYPE_DEFINITION api_type)
 */
export const SCRIPT_TOOL_API_ID = '4d9a83679f9202107c0e2305fa0a1c8c'

/**
 * Tool types supported by the NowAssist Skill API
 * Maps the Fluent API method names to internal type identifiers
 */
export const TOOL_TYPES = {
    INLINE_SCRIPT: 'inline-script',
    SCRIPT: 'script',
    FLOW_ACTION: 'flow-action',
    SUBFLOW: 'subflow',
    SKILL: 'skill',
    WEB_SEARCH: 'web-search',
    DECISION: 'decision',
} as const

/**
 * Invalid internal types for FlowAction, Subflow, and Predictive Intelligence tools.
 * These types are not supported and should be rejected during validation.
 * Matches AI Studio's INVALID_DATATYPES constant.
 *
 * Use internal type values (lowercase, no spaces) as stored in the database:
 * - 'document_id' - Document ID field type
 * - 'reference' - Reference field type (use 'glide_record' instead)
 * - 'snapshot_template_value' - Snapshot template value type
 */
export const INVALID_TOOL_INTERNAL_TYPES = ['document_id', 'reference', 'snapshot_template_value'] as const

/**
 * Valid data types for tool inputs/outputs (OneExtend format).
 * Used for validation of FlowAction, Subflow, and PI tools.
 * Includes both OneExtend types and Flow Designer types that are passed through.
 */
export const VALID_TOOL_DATA_TYPES = [
    // OneExtend types
    'string',
    'boolean',
    'numeric',
    'json_object',
    'json_array',
    'simple_array',
    'glide_record',
    'table',
    'object', // Legacy support - maps to json_object
    // Flow Designer types (passed through as-is)
    'choice',
    'date',
    'datetime',
    'duration',
    'html',
    'conditions',
    'script',
] as const

export const NOW_LLM_GENERIC_PROVIDER_API = '936e514a53b3b110f028ddeeff7b128c'

/**
 * Provider mappings by parent provider name
 * Maps provider name to default provider API and flow implementation
 *
 * User only needs to specify: provider: "Now LLM Service"
 * System automatically resolves: provider_api name + flow implementation
 *
 * Data source: sys_generative_ai_provider_mapping (external=true^active=true)
 */
export const PROVIDER_MAPPINGS: Record<string, { provider_api: string; flow_id: string; flow_type: string }> = {
    // Now LLM Service (ServiceNow's default provider)
    'Now LLM Service': {
        provider_api: 'Now LLM Generic',
        flow_id: '936e514a53b3b110f028ddeeff7b128c',
        flow_type: 'sys_hub_flow',
    },

    // Azure OpenAI (multiple APIs available - using Chat Completions as default)
    'Azure OpenAI': {
        provider_api: 'Chat Completions',
        flow_id: 'a55c1c6f530221106b38ddeeff7b12b1',
        flow_type: 'sys_hub_flow',
    },

    // Open AI
    'Open AI': {
        provider_api: 'Chat Completions',
        flow_id: '3b6a84c4878621104f1c8f59dabb3525',
        flow_type: 'sys_hub_flow',
    },

    // Google Gemini (multiple APIs - using Vertex AI Chat Completions as default)
    'Google Gemini': {
        provider_api: 'Chat Completions',
        flow_id: '9666f54b2dca7110f877b951e3e5bf53',
        flow_type: 'sys_hub_flow',
    },

    // AWS Claude (Amazon Bedrock)
    'AWS Claude': {
        provider_api: 'Amazon Bedrock Chat Completions',
        flow_id: '52f46aeb9f2c5210da502fca9a0a1cda',
        flow_type: 'sys_hub_flow',
    },

    // IBM Watson
    'IBM Watson': {
        provider_api: 'Completions',
        flow_id: 'e841ec9053190210ea08ddeeff7b12ae',
        flow_type: 'sys_hub_flow',
    },

    // Perplexity
    Perplexity: {
        provider_api: 'Perplexity',
        flow_id: '2983f6889fbad21029ead63a6a0a1cd6',
        flow_type: 'sys_hub_flow',
    },

    // Custom LLM Provider
    'Custom LLM Provider': {
        provider_api: 'custom_api',
        flow_id: 'af9702d0c3110210b0939bc8a840dd2c',
        flow_type: 'sys_hub_flow',
    },

    // Aleph Alpha
    'Aleph Alpha': {
        provider_api: 'Completions',
        flow_id: '4c88a81955ea7110f877466af34cb583',
        flow_type: 'sys_hub_flow',
    },
} as const

export const VAR_SET = {
    ENABLE_UI_ACTION: 'f09e3f9893b64210aa5730f16489189f',
    UI_ACTION_ID: '4fee37d893b64210aa5730f164891896',
    ENABLE_UI_BUILDER: 'e3f313ddff6522103a2bffffffffff48',
    ENABLE_NAP: 'ab5ef79893b64210aa5730f16489185a',
    SKILL_BUILDER_DELIVERY_STEP: '7dfdff5893b64210aa5730f164891837',
    CONFIG_TYPE_VIEW_TRIGGER_STEP: 'b89b1f0b7f821210076726d6ac86650e',
    ENABLE_WORKFLOW_TRIGGER: '5c5623df7f025210076726d6ac8665f9',
}

/**
 * ACL-related constants
 * Note: USER_IS_AUTHENTICATED and HAS_ADMIN_ROLE are imported from core/app/Acl.ts
 * to avoid duplication and maintain single source of truth
 */
export const ACL_CONSTANTS = {
    // ACL Type for Gen AI Skills (not in core - specific to NowAssist)
    GEN_AI_SKILL_TYPE: '0d986410ffdd2210c0e9ffffffffffc9',

    // Security Attributes (from core)
    USER_IS_AUTHENTICATED: AclAttributes.user_is_authenticated,
    HAS_ADMIN_ROLE: AclAttributes.has_admin_role,

    // Operations (from core)
    OPERATION_EXECUTE: AclOperations.execute,

    // Decision Types
    DECISION_TYPE_ALLOW: 'allow',
    DECISION_TYPE_DENY: 'deny',

    // Local or Existing
    LOCAL_OR_EXISTING: 'Existing',
} as const

/**
 * Default values for sys_security_acl
 */
export const ACL_DEFAULTS = {
    active: true,
    admin_overrides: true, // needs to verify
    advanced: false,
    decision_type: 'allow',
    local_or_existing: 'Existing',
    operation: 'execute',
} as const

/**
 * Default values for sys_agent_access_role_configuration
 */
export const AGENT_ACCESS_ROLE_CONFIG_DEFAULTS = {
    action: 'limit_to_roles',
    allow_all_session_roles: false,
    agent_table: 'sn_nowassist_skill_config',
} as const

/**
 * Table names for security
 */
export const SECURITY_TABLE_NAMES = {
    SYS_AGENT_ACCESS_ROLE_CONFIGURATION: 'sys_agent_access_role_configuration',
    SYS_AGENT_ACCESS_ROLE_MAPPING: 'sys_agent_access_role_mapping',
    SYS_USER_ROLE: 'sys_user_role',
} as const

/**
 * Maint role sys_id - this role is restricted and cannot be used for ACL or NAP roles
 */
export const MAINT_ROLE_SYS_ID = 'b05947ca0a0a0aa700fdef030c6bca07'

/**
 * Restricted roles that cannot be used for ACL or NowAssistPanel roles
 * These roles have special system-level privileges that should not be assigned to skills
 */
export const RESTRICTED_ROLE_NAMES = ['maint'] as const
export const RESTRICTED_ROLE_SYS_IDS = [MAINT_ROLE_SYS_ID] as const

/**
 * Validation constants
 */
export const SKILL_NAME_MAX_LENGTH = 40
export const MAX_TOKENS_UPPER_BOUND = 130722

/**
 * Tool resource capability sys_ids
 * These are the sys_ids of the resource capabilities in sys_one_extend_capability table
 * that define the different tool types available in NowAssist skills
 */
export const TOOL_RESOURCE_CAPABILITIES = {
    INLINE_SCRIPT: '51d095f77f211210210ca65c4d866591',
    WEB_SEARCH: '5817eb2b9f36121029ead63a6a0a1c7c',
    DECISION: '5cedd7147ff1921032d293f10d866506',
    SKILL: '51d095f77f211210210ca65c4d866595', // Skill-as-Tool
} as const

/**
 * Tool attribute sys_ids (inputs and outputs)
 * These are the sys_ids of the definition attributes for tool inputs and outputs
 */
export const TOOL_ATTRIBUTE_IDS = {
    // InlineScript
    INLINE_SCRIPT_INPUT: '0bd1117b7f211210210ca65c4d8665ed', // "inputscript" attribute
    INLINE_SCRIPT_OUTPUT: 'a3911d3b7f211210210ca65c4d86651b', // "output" attribute
} as const

/**
 * WebSearch tool input/output mapping configurations.
 * Maps fluent camelCase property names to platform snake_case names and their
 * sys_one_extend_definition_attribute sys_ids.
 */
export interface WebSearchInputMapping {
    fluentName: string
    platformName: string
    attributeId: string
    required: boolean
    default?: string | number
}

export interface WebSearchOutputMapping {
    name: string
    attributeId: string
}

export const WEBSEARCH_AI_ANSWERS_CONFIG = {
    INPUT_MAPPINGS: [
        { fluentName: 'query', platformName: 'query', attributeId: '9659ebef9f36121029ead63a6a0a1ce8', required: true },
        {
            fluentName: 'searchType',
            platformName: 'search_type',
            attributeId: 'c11ece269fc3121029ead63a6a0a1cd2',
            required: true,
        },
        {
            fluentName: 'aiSearchProviders',
            platformName: 'aisearch_providers',
            attributeId: 'bcfc3961ffad6210a81effffffffffe0',
            required: false,
            default: '',
        },
    ] as const satisfies readonly WebSearchInputMapping[],
    OUTPUT_MAPPINGS: [
        { name: 'provider', attributeId: 'a017ef2b9f36121029ead63a6a0a1cec' },
        { name: 'response', attributeId: 'a417ef2b9f36121029ead63a6a0a1cee' },
        { name: 'error', attributeId: '2c17ef2b9f36121029ead63a6a0a1cda' },
        { name: 'errorcode', attributeId: 'ac17ef2b9f36121029ead63a6a0a1ce9' },
        { name: 'status', attributeId: 'e017ef2b9f36121029ead63a6a0a1cf3' },
    ] as const satisfies readonly WebSearchOutputMapping[],
} as const

export const WEBSEARCH_SCRAPING_CONFIG = {
    INPUT_MAPPINGS: [
        { fluentName: 'query', platformName: 'query', attributeId: '9659ebef9f36121029ead63a6a0a1ce8', required: true },
        {
            fluentName: 'searchType',
            platformName: 'search_type',
            attributeId: 'c11ece269fc3121029ead63a6a0a1cd2',
            required: true,
        },
        {
            fluentName: 'numResults',
            platformName: 'num',
            attributeId: '790a67639f76121029ead63a6a0a1c7b',
            required: false,
            default: 3,
        },
        {
            fluentName: 'sites',
            platformName: 'site',
            attributeId: 'e03aa7639f76121029ead63a6a0a1c35',
            required: false,
            default: '',
        },
        {
            fluentName: 'country',
            platformName: 'country',
            attributeId: '3e5a67639f76121029ead63a6a0a1c65',
            required: false,
            default: '',
        },
        {
            fluentName: 'maxTokens',
            platformName: 'max_tokens',
            attributeId: '5aa03aba9f0f121029ead63a6a0a1c32',
            required: false,
            default: 1000,
        },
    ] as const satisfies readonly WebSearchInputMapping[],
    OUTPUT_MAPPINGS: [
        { name: 'provider', attributeId: 'a017ef2b9f36121029ead63a6a0a1cec' },
        { name: 'response', attributeId: 'a417ef2b9f36121029ead63a6a0a1cee' },
        { name: 'error', attributeId: '2c17ef2b9f36121029ead63a6a0a1cda' },
        { name: 'errorcode', attributeId: 'ac17ef2b9f36121029ead63a6a0a1ce9' },
        { name: 'status', attributeId: 'e017ef2b9f36121029ead63a6a0a1cf3' },
    ] as const satisfies readonly WebSearchOutputMapping[],
} as const

/**
 * Skill-as-Tool configuration
 * Skill tools have 5 fixed outputs (same as WebSearch): provider, response, error, errorcode, status
 */
export const SKILL_TOOL_CONFIG = {
    /** Fixed output names for Skill tools - always created */
    FIXED_OUTPUT_NAMES: ['provider', 'response', 'error', 'errorcode', 'status'] as const,
    /** Map output names to FixedOutputTruncateConfig keys */
    OUTPUT_NAME_TO_TRUNCATE_KEY: {
        provider: 'provider',
        response: 'response',
        error: 'error',
        errorcode: 'errorcode',
        status: 'status',
    } as const,
} as const

/**
 * AI Search Providers lookup values (from sys_one_extend_capability_attribute_resource_lookup)
 * Maps simple Fluent values to full JSON objects required by platform
 */
export const AI_SEARCH_PROVIDERS = {
    perplexity: {
        value: 'perplexity',
        label: 'Perplexity',
        capId: 'd569b70d9fa2521029ead63a6a0a1c11',
        pId: '2983f6889fbad21029ead63a6a0a1cd6',
    },
    openai: {
        value: 'openai',
        label: 'OpenAI',
        capId: 'd569b70d9fa2521029ead63a6a0a1c11',
        pId: 'd54762c77fd42210a3aed719dc8665c3',
    },
    gemini: {
        value: 'gemini',
        label: 'Gemini',
        capId: 'd569b70d9fa2521029ead63a6a0a1c11',
        pId: '9666f54b2dca7110f877b951e3e5bf53',
        isDefault: true,
    },
    azure_openAI: {
        value: 'azure_openAI',
        label: 'Azure Open AI',
        capId: 'd569b70d9fa2521029ead63a6a0a1c11',
        pId: 'b4a8cb46ffa82210a3aeffffffffff9a',
    },
} as const

/**
 * Default role sys_id for roleRestrictions (admin role)
 * Used as fallback when no role restrictions are specified
 * Source: sys_user_role table (admin role)
 */
export const DEFAULT_ADMIN_ROLE_SYS_ID = '2831a114c611228501d4ea6c309d626d'

/**
 * Default values for sys_one_extend_resource_mapping table
 * TABLE_DEFAULTS contains the subset used by applyDefaults().
 * This constant adds script/metadata defaults that need special handling (e.g., toCdata).
 */
export const RESOURCE_MAPPING_DEFAULTS = {
    active: true,
} as const

/**
 * Default applicability script for resource mappings
 */
export const RESOURCE_MAPPING_APPLICABILITY_SCRIPT = `(function(currentInputs, context) {
    /* @param {Object} currentInputs - Inputs provided for the current step.*/
    /* @param {Object} context - Execution context, which may include:*/
    /*                         - Previous tool outputs/inputs (e.g., context['ToolName.attributeName'] or context.getValue('ToolName.attributeName')).*/
    /*                         - Additional context information (e.g., context.getAllValues()).*/
    /* @returns {Boolean} - Return 'true' if the condition is met; otherwise 'false'.*/
    return true;
})(currentInputs, context);`

/**
 * Default attribute value script for resource attribute mappings
 */
export const RESOURCE_ATTRIBUTE_VALUE_SCRIPT = `(function execute(currentInputs, context) {
    /* @param {Object} currentInputs - Inputs provided for the current step.*/
    /* @param {Object} context - Execution context, which may include:*/
    /*                         - Previous tool outputs/inputs (e.g., context['ToolName.attributeName'] or context.getValue('ToolName.attributeName')).*/
    /*                         - Additional context information (e.g., context.getAllValues()).*/
    /* @returns {Object} - The computed value for the attribute.*/
    return '';
})(currentInputs, context);`

/**
 * Default applicability script for resource edges
 */
export const RESOURCE_EDGE_APPLICABILITY_SCRIPT = `(function(currentInputs, context) {  
    /* @param {Object} currentInputs - Inputs provided for the current step.*/  
    /* @param {Object} context - Execution context, which may include:*/     
    /*         - Previous tool outputs/inputs (e.g., context['ToolName.attributeName'] or context.getValue('ToolName.attributeName')).*/     
    /*         - Additional context information (e.g., context.getAllValues()).*/  
    /* @returns {Boolean} - Return 'true' if the condition is met; otherwise 'false'.*/  
    return true;
  })(currentInputs, context);`

/**
 * Default error handler script for resource edges
 */
export const RESOURCE_EDGE_ERROR_HANDLER_SCRIPT = `(function handleError() {  
    // customize condition logic here,  
    return ;
})();`

/**
 * Unsupported tool capability IDs
 */
export const UNSUPPORTED_TOOL_CAPABILITY_IDS = new Set([
    '739b26e553e5e21016f7ddeeff7b1237', // DocIntel Tool
    '5c6292cdff675210f465ffffffffffd9', // Predictive Intelligence Tool
    // Retriever Search Tool
    '5345d14277e81210e9c41345ba5a9933',
    '2da6bcffa30012104429a138e31e61ef',
    '69e287e7431202101ed803295bb8f285',
    // Websearch (maint only) - from sn_ai_websearch
    'a3e7b0189fe2121029ead63a6a0a1c90', // Web Search APIs
    'f04ccd149f62121029ead63a6a0a1c51', // Web Scraping APIs
    '759c5b709faa121029ead63a6a0a1c45', // QnA
    'd569b70d9fa2521029ead63a6a0a1c11', // AI Search answers
])

/**
 * Excluded capability IDs to filter out from resource mappings
 * These are evaluation metrics that should not be processed as tools
 * Source: sys_one_extend_eval_suggestion (capability_type=skill)
 */
export const EXCLUDED_METRIC_CAPABILITY_IDS = new Set([
    // Evaluation metrics
    'fbf714e97f41121046719fbefc866574', // Completeness Metric
    '8ccacaa77f1202105bc49fbefc86656d', // Recall@K Metric
    'f5fa89d57f14121071cab6257d8665a6', // Correctness Metric with Golden Response
    'e45b7c297f92121071cab6257d86651e', // Fluency JoJ metric
    '53fed71f7fad521071cab6257d8665c6', // Offensiveness Guardian Metric
    '885e57732bdb1e10802dfd78ce91bf05', // Response Faithfulness
    '71114a5d3bf7a210205eb7e643e45a75', // ERR Faithfulness Metric
    'b1a4347e3bd1aa10654f4fb643e45a0c', // DocIntel Key Extraction metric
    'f74fd7b32bdb1e10802dfd78ce91bfad', // VA Actions Helpfulness
    'a28f0e067f0202105bc49fbefc8665ec', // Correctness Metric
    '41f9b88eff892210cc64ffffffffffa9', // Number of Words Metric
    '6a1e78b2938426105bc8d455748918b1', // NumberOfTurnsMetric
    'c49fb05247bf22100ebeeb40706d4308', // ERR Fluency Metric
    '605d6c0f7ffe02100a03b6257d8665d3', // Fluency Metric
    'f22bf8657f52121071cab6257d8665e7', // Correctness w Golden Response JoJ metric
    '954ad0599393a610f3a0b8027cba10fe', // KM Fluency Metric
    '729c4ad33b195210e3ddac9a04e45af2', // (Experimental) QnA Relevancy Metric
    'ab055042a37b1210883c25d1d71e61f0', // VA Actions Match Metric
    '3de042ab7f9602105bc49fbefc866571', // Precision@K Metric
    '82900c917fd6521071cab6257d86658f', // Faithfulness JoJ metric
    '63015aeda3920210883c25d1d71e6124', // Faithfulness Metric
    '775b7ca57f92121071cab6257d86657b', // Correctness JoJ metric
    '222a5bfb2b9b1e10802dfd78ce91bf1c', // Response Level Fluency
    '8d0964757f9202105bc49fbefc8665f7', // F1Score@K Metric
    '864066e7a3d64210883c25d1d71e61e7', // (Experimental) Helpfulness Metric
    '625fe4e97f12121071cab6257d86658a', // Completeness JoJ metric
    '2f1e2fea9373e210c64cf5559803d668', // ERR Relevancy Metric
    '39889392a3760210883c25d1d71e617f', // MatchMetric
    'bcdfdfe69333e210c64cf5559803d68c', // ERR Completeness Metric
    'ac7364442b8656105866f6046e91bfb9', // Task Completeness with Ground truth
])

// ==========================================
// Target-specific exports (used by attribute-processor.ts, provider-processor.ts, etc.)
// ==========================================

// Default field values for sys_gen_ai_skill table
export const SYS_GEN_AI_SKILL_DEFAULTS = {
    enable_followup: false,
    skill_description: '',
    skill_table: 'sys_one_extend_capability',
    sys_domain: 'global',
    sys_domain_path: '/',
    sys_overrides: '',
} as const

// Default condition_script for sys_gen_ai_skill_applicability table
// This script is used when condition_mode is 'simple' and must be wrapped in CDATA during serialization
export const DEFAULT_CONDITION_SCRIPT =
    '(function executeCondition(/* glide record */ current) {   \n\t// customize condition logic here, return true or false   \n\treturn false;\n})(current);'

// Default field values for sys_gen_ai_skill_applicability table
export const SYS_GEN_AI_SKILL_APPLICABILITY_DEFAULTS = {
    condition_mode: 'simple',
} as const

// Default field values for sys_gen_ai_skill_config table
export const SYS_GEN_AI_SKILL_CONFIG_DEFAULTS = {
    channels: '',
    discoverable: true,
    supervision: '',
    use_knowledge_graph: false,
    visible: true,
    sys_domain: 'global',
    sys_domain_path: '/',
    sys_overrides: '',
} as const

/**
 * Standard output attribute definitions sourced from AI Studio's _getOutputAttributes() in SkillDesigner.
 * Used in both build (skill-processor) and transform (transform-logic) flows.
 */
export const STANDARD_OUTPUT_NAMES = ['response', 'provider', 'errorcode', 'status', 'error'] as const

export const STANDARD_OUTPUT_DEFAULTS: Record<string, { label: string; description: string; dataType: string }> = {
    response: { label: 'response', description: 'Response Text', dataType: 'string' },
    provider: { label: 'provider', description: 'Provider name', dataType: 'string' },
    errorcode: { label: 'errorCode', description: 'Error Code', dataType: 'string' },
    status: { label: 'status', description: 'Status', dataType: 'string' },
    error: { label: 'error', description: 'Error Message', dataType: 'string' },
} as const

export const ALLOWED_DATATYPES_FOR_NAP = ['glide_record', 'numeric', 'boolean', 'string']

/**
 * Supported data types for filterCondition in prompt versions
 * Only inputs with these data types can be used in filter conditions
 */
export const SUPPORTED_FILTER_CONDITION_DATATYPES = ['string', 'numeric', 'boolean', 'glide_record'] as const

/**
 * Invalid data types for subflow and flowaction tools
 * These data types are not supported for definition attributes in subflow/flowaction tools
 */
export const INVALID_DATATYPES_FOR_SUBFLOW_AND_FLOWACTION = ['Document ID', 'Reference', 'Snapshot Template Value']

/**
 * Default preprocessor script for providers
 */
export const DEFAULT_PREPROCESSOR_SCRIPT = `(function(inputs) {
    /* write code here to transform capability input to definition input.*/

    inputs = JSON.parse(inputs);

    return inputs;
})(inputs);`

/**
 * Default postprocessor script for providers
 */
export const DEFAULT_POSTPROCESSOR_SCRIPT = `(function(outputs) {
    /* write code here to transform definition output to capability output.*/

    outputs = JSON.parse(outputs);

    return outputs;
})(outputs);`
